import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Plus,
  Code,
  FileCode,
  Brush,
  Book,
  Download,
  Delete,
  FolderOpen,
  HardDrive,
  Loader2,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Attachment, QueuedChatMessage } from '@/types';
import ChatInputField from './ChatInputField';
import { useKnowledgeBases } from '@/hooks/useKnowledgeBases';
import KnowledgeBaseActiveIndicator from './KnowledgeBaseActiveIndicator';
import { useChatInput } from '@/hooks/useChatInput';
import InputAttachmentPreview from './InputAttachmentPreview';
import MapleOSFilePickerDialog from './MapleOSFilePickerDialog';
import { toast } from '@/hooks/use-toast';
import { createAttachment, readFileContent } from '@/utils/chatUtils';
import { globalVFS } from '@/OS/utils/storage/VFS';
import type { FileMetadata } from '@/OS/utils/storage/StorageDriver';
import QueuedMessagesBar from './QueuedMessagesBar';
import QueuedMessagesPanel from './QueuedMessagesPanel';

interface SimpleChatInputProps {
  onSendMessage: (message: string, attachments?: Attachment[], kbId?: string | null, kbName?: string | null) => void;
  onClearChat?: () => void;
  isLoading: boolean;
  isGenerating?: boolean;
  allowQueueWhileLoading?: boolean;
  queuedMessages?: QueuedChatMessage[];
  onRemoveQueuedMessage?: (id: string) => void;
  onStopGeneration?: () => void;
  onOpenCodeEditor?: () => void;
  onOpenCanvas?: () => void;
  onOpenCodeHistory?: () => void;
  onFileUpload?: (file: File) => void;
  onExportChat?: () => void;
  chatId?: string;
}

const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico']);
const AUDIO_EXTENSIONS = new Set(['mp3', 'wav', 'm4a', 'ogg', 'webm', 'aac', 'flac']);
const TEXT_EXTENSIONS = new Set([
  'txt',
  'md',
  'json',
  'csv',
  'ts',
  'tsx',
  'js',
  'jsx',
  'css',
  'html',
  'xml',
  'yml',
  'yaml',
  'py',
  'java',
  'c',
  'cpp',
  'h',
  'hpp',
  'rs',
  'go',
  'sql',
  'sh',
  'bat',
  'ps1',
  'log',
  'ini',
  'toml',
]);
const DOCUMENT_EXTENSIONS = new Set(['pdf', 'doc', 'docx', 'odt', 'rtf', 'xls', 'xlsx', 'ods', 'ppt', 'pptx', 'odp']);

const getExtension = (fileName: string) => fileName.split('.').pop()?.toLowerCase() || '';

const buildBinaryPlaceholder = (fileName: string, sizeInBytes?: number, mimeType?: string) => {
  const fileType = mimeType || getExtension(fileName) || 'unknown';
  const sizeText = typeof sizeInBytes === 'number' ? `${(sizeInBytes / 1024).toFixed(2)} KB` : 'Unknown size';

  return `[This is a binary file: ${fileName}]\n\nFile size: ${sizeText}\nFile type: ${fileType}\n\nThis file has been attached from storage, but its raw binary content is not displayed as text.`;
};

const fileToDataUrl = async (file: File) => {
  const reader = new FileReader();

  return await new Promise<string>((resolve, reject) => {
    reader.onload = (event) => {
      if (typeof event.target?.result === 'string') {
        resolve(event.target.result);
        return;
      }

      reject(new Error('Failed to read file'));
    };

    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

const createMapleOsAttachment = (file: FileMetadata, content: string): Attachment => {
  const extension = getExtension(file.path);
  const fileName = file.path.split('\\').pop() || file.path;
  const trimmedContent = content.trim();

  if (IMAGE_EXTENSIONS.has(extension)) {
    if (content.startsWith('data:image/')) {
      return createAttachment('image', content, fileName);
    }

    if (extension === 'svg' && trimmedContent.startsWith('<svg')) {
      return createAttachment('image', `data:image/svg+xml;charset=utf-8,${encodeURIComponent(content)}`, fileName);
    }
  }

  if (AUDIO_EXTENSIONS.has(extension) && content.startsWith('data:audio/')) {
    return createAttachment('audio', content, fileName);
  }

  return createAttachment('file', content || buildBinaryPlaceholder(fileName, file.size), fileName);
};

const SimpleChatInput: React.FC<SimpleChatInputProps> = ({
  onSendMessage,
  onClearChat,
  isLoading,
  isGenerating = false,
  allowQueueWhileLoading = false,
  queuedMessages = [],
  onRemoveQueuedMessage,
  onStopGeneration,
  onOpenCodeEditor,
  onOpenCanvas,
  onOpenCodeHistory,
  onFileUpload,
  onExportChat,
  chatId,
}) => {
  const { fetchKnowledgeBases } = useKnowledgeBases();
  const [isUploading, setIsUploading] = useState(false);
  const [isMapleOsPickerOpen, setIsMapleOsPickerOpen] = useState(false);
  const [isQueueExpanded, setIsQueueExpanded] = useState(false);

  const disableForLoading = isLoading && !allowQueueWhileLoading;
  const disableComposer = disableForLoading || isUploading;
  const disableSubmit = isUploading;

  const {
    message,
    pendingAttachments,
    setPendingAttachments,
    textareaRef,
    fileInputRef,
    activeKnowledgeBase,
    handleSubmit,
    handleKeyDown,
    handleTextareaChange,
    handleFileSelect,
    handleRemoveAttachment,
    handleSelectKnowledgeBase,
    clearKnowledgeBase,
    setMessage,
  } = useChatInput(
    onSendMessage,
    onClearChat,
    onOpenCodeEditor,
    onOpenCanvas,
    onExportChat,
    isLoading,
    chatId,
    allowQueueWhileLoading
  );

  useEffect(() => {
    fetchKnowledgeBases();
  }, [fetchKnowledgeBases]);

  useEffect(() => {
    if (queuedMessages.length === 0) {
      setIsQueueExpanded(false);
    }
  }, [queuedMessages.length]);

  const attachNativeFile = async (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Maximum file size is 10 MB',
        variant: 'destructive',
      });
      return;
    }

    const extension = getExtension(file.name);
    const isImage = file.type.startsWith('image/');
    const isAudio = file.type.startsWith('audio/') || AUDIO_EXTENSIONS.has(extension);
    const isTextLike =
      TEXT_EXTENSIONS.has(extension) ||
      file.type.startsWith('text/') ||
      file.type === 'application/json' ||
      file.type === 'application/xml';
    const isDocument = DOCUMENT_EXTENSIONS.has(extension) || file.type === 'application/pdf';

    setIsUploading(true);

    try {
      let attachment: Attachment;

      if (isImage) {
        attachment = createAttachment('image', await fileToDataUrl(file), file.name);
      } else if (isAudio) {
        attachment = createAttachment('audio', await fileToDataUrl(file), file.name);
      } else if (isTextLike || isDocument) {
        const content = await readFileContent(file);
        attachment = createAttachment('file', content || buildBinaryPlaceholder(file.name, file.size, file.type), file.name);
      } else {
        attachment = createAttachment('file', buildBinaryPlaceholder(file.name, file.size, file.type), file.name);
      }

      setPendingAttachments((previous) => [...previous, attachment]);
      onFileUpload?.(file);
    } catch (error) {
      console.error('Error uploading native file:', error);
      toast({
        title: 'Upload failed',
        description: 'There was an error attaching your file.',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    await attachNativeFile(file);
  };

  const handleMapleOsFileSelect = async (file: FileMetadata) => {
    setIsUploading(true);

    try {
      const content = await globalVFS.load(file.path);
      if (content === null) {
        throw new Error('Unable to load file from MapleOS');
      }

      const attachment = createMapleOsAttachment(file, content);
      setPendingAttachments((previous) => [...previous, attachment]);
      setIsMapleOsPickerOpen(false);
    } catch (error) {
      console.error('Error attaching MapleOS file:', error);
      toast({
        title: 'Attach failed',
        description: 'There was an error loading that MapleOS file.',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const canManageQueue = queuedMessages.length > 0 && allowQueueWhileLoading;

  const composerForm = (
    <form onSubmit={handleSubmit} className="flex items-end gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="outline" size="icon" className="shrink-0" disabled={disableComposer}>
            {isUploading ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <DropdownMenuItem onClick={handleFileSelect} disabled={disableComposer}>
            <HardDrive className="mr-2 h-4 w-4" />
            <span>Attach from Native OS</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setIsMapleOsPickerOpen(true)} disabled={disableComposer}>
            <FolderOpen className="mr-2 h-4 w-4" />
            <span>Attach from MapleOS</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onOpenCodeEditor} disabled={!onOpenCodeEditor || disableComposer}>
            <Code className="mr-2 h-4 w-4" />
            <span>Code editor</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onOpenCodeHistory} disabled={!onOpenCodeHistory || disableComposer}>
            <FileCode className="mr-2 h-4 w-4" />
            <span>Code history</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onOpenCanvas} disabled={!onOpenCanvas || disableComposer}>
            <Brush className="mr-2 h-4 w-4" />
            <span>Canvas</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              setMessage('/kb ');
              setTimeout(() => {
                if (textareaRef.current) {
                  textareaRef.current.focus();
                  const length = textareaRef.current.value.length;
                  textareaRef.current.setSelectionRange(length, length);
                }
              }, 0);
            }}
            disabled={disableComposer}
          >
            <Book className="mr-2 h-4 w-4" />
            <span>Use knowledge base</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onExportChat} disabled={!onExportChat || disableComposer}>
            <Download className="mr-2 h-4 w-4" />
            <span>Export chat</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onClearChat} disabled={!onClearChat || disableComposer} className="text-destructive">
            <Delete className="mr-2 h-4 w-4" />
            <span>Clear chat</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="flex-1">
        <ChatInputField
          message={message}
          textareaRef={textareaRef}
          hasAttachments={pendingAttachments.length > 0}
          disableInput={disableComposer}
          disableSubmit={disableSubmit}
          isGenerating={isGenerating}
          onStopGeneration={onStopGeneration}
          onTextChange={handleTextareaChange}
          onKeyDown={handleKeyDown}
          onSubmit={handleSubmit}
          onSelectKnowledgeBase={handleSelectKnowledgeBase}
        />
      </div>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        accept="image/*,audio/*,.txt,.md,.json,.csv,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
      />
    </form>
  );

  return (
    <>
      <div className="border-t border-border bg-background p-3">
        <div className="space-y-2">
          {activeKnowledgeBase ? (
            <div className="px-2">
              <KnowledgeBaseActiveIndicator knowledgeBaseName={activeKnowledgeBase} onClear={clearKnowledgeBase} />
            </div>
          ) : null}

          {pendingAttachments.length > 0 ? (
            <div className="px-2">
              <InputAttachmentPreview attachments={pendingAttachments} onRemoveAttachment={handleRemoveAttachment} />
            </div>
          ) : null}

          {canManageQueue ? (
            <div className="overflow-hidden rounded-2xl border border-border/70 bg-background shadow-sm">
              {isQueueExpanded ? (
                <QueuedMessagesPanel
                  queuedMessages={queuedMessages}
                  onRemove={(id) => onRemoveQueuedMessage?.(id)}
                  onCollapse={() => setIsQueueExpanded(false)}
                />
              ) : (
                <QueuedMessagesBar queuedMessages={queuedMessages} onExpand={() => setIsQueueExpanded(true)} />
              )}

              <div className="p-2">{composerForm}</div>
            </div>
          ) : (
            composerForm
          )}

          {isUploading ? (
            <div className="mt-1 flex items-center px-2 text-xs text-muted-foreground">
              <Loader2 size={12} className="mr-1 animate-spin" />
              <span>Attaching file...</span>
            </div>
          ) : null}
        </div>
      </div>

      <MapleOSFilePickerDialog
        open={isMapleOsPickerOpen}
        onOpenChange={setIsMapleOsPickerOpen}
        onSelectFile={(file) => void handleMapleOsFileSelect(file)}
        selecting={isUploading}
      />
    </>
  );
};

export default SimpleChatInput;